import { Box, Button, Container, Grid, GridItem, Select, Switch, Textarea } from '@chakra-ui/react';
import { isFalse } from 'my-easy-fp';
import React from 'react';
import { MdArrowDropDown } from 'react-icons/md';
import { useRecoilState, useRecoilValue } from 'recoil';
import { Subject } from 'rxjs';
import { debounceTime, share } from 'rxjs/operators';
import { textCounterAtom, textLayoutAtom, windowSizeAtom } from '../recoil/atom/counter';
import { charCountState, onlyKoreanCharCountSelector } from '../recoil/selector/counter';
import ContentBox from '../shared/components/ContentBox';
import ControlBox from '../shared/components/ControlBox';
import ResizeBox from '../shared/components/ResizeBox';
import { getKorean, getLetterLayout, getMarginSize, getRectSize } from '../tools/calculator';
import llc from '../tools/llc';

const log = llc(__filename);

const Counter: React.FC = () => {
  const [textCounterState, setTextCounterState] = useRecoilState(textCounterAtom);
  const [textLayoutState, setTextLayoutState] = useRecoilState(textLayoutAtom);
  const [windowSizeState] = useRecoilState(windowSizeAtom);
  const counted = useRecoilValue(charCountState);
  const koreanChars = useRecoilValue(onlyKoreanCharCountSelector);

  const subject = new Subject<string>();
  const shared = subject.pipe(share());

  shared.pipe(debounceTime(1000)).subscribe((topic) => {
    if (topic.length < 10) {
      return;
    }

    if (getKorean(topic).length < 10) {
      return;
    }

    const letters = getLetterLayout(topic);
    const [maxColSize] = letters.map((layout) => layout.length).sort((left, right) => right - left);
    const size = getMarginSize({ letters, size: { width: windowSizeState.width, height: windowSizeState.height } });
    const { widths, heights } = getRectSize({
      size: { width: size.width, height: windowSizeState.height },
      letters,
      margin: {
        left: size.margin.left,
        right: size.margin.right,
      },
    });

    log('레이아웃 저장함 :) ', size.margin);

    setTextLayoutState({
      ...textLayoutState,
      maxColSize,
      maxRowSize: letters.length,
      letters: letters,
      character: {
        widths,
        heights,
        margin: size.margin,
      },
    });
  });

  shared.subscribe((topic) => {
    setTextCounterState({ ...textCounterState, content: topic });
  });

  return (
    <React.Fragment>
      <Container maxW="container.lg">
        <Box h="20px" />

        <Box>
          <Textarea value={textCounterState.content} rows={10} onChange={(event) => subject.next(event.target.value)} />

          <Box h="10px" />

          <Grid templateColumns="repeat(6, 1fr)" templateRows="repeat(3, 1fr)" gap={4}>
            <GridItem colSpan={4}>
              <Select
                icon={<MdArrowDropDown />}
                value={textCounterState.charCountType}
                onChange={(event) => {
                  const charCountType = parseInt(event.target.value, 10);
                  setTextCounterState({
                    ...textCounterState,
                    charCountType: isFalse(isNaN(charCountType)) ? charCountType : 1,
                  });
                }}
              >
                <option value="1">모든 글자 1글자로 계산</option>
                <option value="2">한글 1글자, 영문 0.5글자 계산</option>
                <option value="3">바이트로 계산, 한글 2글자 영문 1글자</option>
                <option value="4">바이트로 계산, 한글 3글자 영문 1글자</option>
              </Select>
            </GridItem>

            <GridItem rowSpan={2} colSpan={2}>
              <section>
                <span>{counted}글자</span>
                <br />
                <span>{koreanChars}글자(한글)</span>
              </section>
            </GridItem>

            <GridItem colSpan={4}>
              <Select
                value={textCounterState.newlineCountType}
                onChange={(event) => {
                  const newlineCountType = parseInt(event.target.value, 10);
                  setTextCounterState({
                    ...textCounterState,
                    newlineCountType: isFalse(isNaN(newlineCountType)) ? newlineCountType : 1,
                  });
                }}
              >
                <option value="1">줄바꿈 0으로 계산</option>
                <option value="2">줄바꿈 1로 계산</option>
                <option value="3">줄바꿈 2로 계산</option>
              </Select>
            </GridItem>

            <GridItem colSpan={4}>
              <Switch
                isChecked={textCounterState.isSkipWhitespacee}
                onChange={() => {
                  setTextCounterState({
                    ...textCounterState,
                    isSkipWhitespacee: isFalse(textCounterState.isSkipWhitespacee),
                  });
                }}
              />
              {' 띄어쓰기 제외'}
            </GridItem>

            <GridItem>
              <Button
                colorScheme="teal"
                size="md"
                onClick={() => {
                  subject.next(textCounterState.content);
                }}
              >
                다시 계산하기
              </Button>
            </GridItem>
          </Grid>

          <Box h="10px" />
        </Box>
      </Container>

      <ControlBox />
      <ContentBox />
      <ResizeBox />
    </React.Fragment>
  );
};

export default Counter;
